import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { SearchInput } from '@/components/ui/Input'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { useCompanies } from '@/hooks/useCompanies'
import type { Company } from '@/lib/pocketbase'

export function CompaniesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') || ''
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading, error } = useCompanies({ search, page, perPage: 20 })

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('q', value)
    } else {
      params.delete('q')
    }
    params.set('page', '1')
    setSearchParams(params)
  }

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="บริษัท"
        description="เรียกดูบริษัทที่เข้าร่วมการประมูลภาครัฐ"
      />

      {/* Search */}
      <div className="mb-6">
        <SearchInput
          placeholder="ค้นหาด้วยชื่อบริษัทหรือเลขประจำตัวผู้เสียภาษี..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Results info */}
      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-48 bg-slate-200 rounded" />
                  <div className="h-3 w-32 bg-slate-200 rounded" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <Card className="p-6 text-center">
          <Icon icon="solar:danger-circle-line-duotone" className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">โหลดบริษัทไม่สำเร็จ</h3>
          <p className="text-sm text-slate-500 mt-1">กรุณาลองใหม่ภายหลัง</p>
        </Card>
      )}

      {/* Empty */}
      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:buildings-2-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบบริษัท</h3>
          <p className="text-sm text-slate-500 mt-1">
            ลองปรับคำค้นหาของคุณ
          </p>
        </Card>
      )}

      {/* Company list */}
      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((company) => (
              <CompanyCard key={company.id} company={company} showDetails />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function CompanyCard({ company, showDetails }: { company: Company; showDetails: boolean }) {
  const initials = company.name
    ?.split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'

  return (
    <Link to={`/companies/${company.id}`}>
      <Card hover className="group">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
            {initials}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors">
              {company.name}
            </h3>
            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
              {company.tax_id && (
                <span className="flex items-center gap-1.5">
                  <Icon icon="solar:card-line-duotone" className="w-4 h-4 text-slate-400" />
                  {company.tax_id}
                </span>
              )}
              {showDetails && company.address && (
                <span className="truncate">{company.address}</span>
              )}
            </div>
          </div>

          {/* Arrow */}
          <Icon icon="solar:alt-arrow-right-line-duotone" className="w-5 h-5 text-slate-300" />
        </div>
      </Card>
    </Link>
  )
}
